import { lazy } from "react";

/**
 * lazyWithRetry — protege contra `ChunkLoadError` e contra chunk desatualizado
 * sem `default` quando o bundle do cliente está velho (dev hot-reload, deploys
 * de produção, cache de Service Worker, etc.).
 *
 * Estratégia (padrão amplamente usado):
 *  1. Tenta o import dinâmico normalmente.
 *  2. Se falhar e ainda não tivermos tentado um reload nesta sessão,
 *     marca a sessionStorage e força `window.location.reload()`. O reload
 *     traz um `bundle.js` novo, que aponta para os chunks corretos.
 *  3. Se a falha persistir após o reload (problema real — chunk ausente
 *     no servidor, falha de rede), propaga o erro para o ErrorBoundary
 *     do Suspense — evitamos loop infinito de reload.
 *
 * Uso (drop-in replacement):
 *   const HomePage = lazyWithRetry(() => import("./pages/HomePage"));
 */
const SESSION_FLAG = "lazy-retry-reloaded";
const STALE = "STALE_CHUNK_NO_DEFAULT";

export function lazyWithRetry(componentImport) {
  return lazy(async () => {
    const hasReloaded = window.sessionStorage.getItem(SESSION_FLAG) === "1";
    try {
      const mod = await componentImport();
      // Chunk desatualizado consegue carregar, mas volta sem `default`. O React
      // quebra com "Received a promise that resolves to: undefined", então
      // tratamos igual a uma falha de chunk e recarregamos uma vez.
      if (!mod || typeof mod.default === "undefined") {
        const err = new Error(STALE);
        err.name = "ChunkLoadError";
        throw err;
      }
      // Sucesso — limpa o flag pra próximas falhas terem direito a 1 retry
      window.sessionStorage.removeItem(SESSION_FLAG);
      return mod;
    } catch (err) {
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        err?.message === STALE ||
        /Loading chunk .* failed/i.test(err?.message || "") ||
        /Loading CSS chunk .* failed/i.test(err?.message || "");

      if (isChunkError && !hasReloaded) {
        window.sessionStorage.setItem(SESSION_FLAG, "1");
        // Reload com cache bypass — pega bundle.js fresco
        window.location.reload();
        // Retorna uma promise que nunca resolve, evitando flash de erro
        return new Promise(() => {});
      }
      throw err;
    }
  });
}

export default lazyWithRetry;
